import { cloneDeep, omit } from "lodash";
import {
  BaseFormField,
  IAdGroup,
  ICampaign,
  ICampaignAPI,
  ICampaignFormState,
  ICampaignResponse,
  IForm,
  IFormFieldRecord,
} from "../../App/Typings/Campaigns";
import { CampaignContact, CampaignTypes } from "../../App/Enums/CampaignTypes";
import {
  dateAddedFormField,
  defaultFormFieldOrder,
  defaultJATNFormFieldOrder,
  emailFormField,
  phoneFormField,
} from "../../App/Constants/CampaignConstants";
import { maxCampaignNameLength } from "../Schema/CampaignFormSchema";
import { InitialValues, defaultFormFields } from "../State/FormCreateInitialState";

/**
 * Converts dates from JSON string to Date objects
 *
 * @param candidate ICampaign with dates in string format
 * @returns ICampaign with date objects
 */
function convertCampaignDates(campaign: ICampaignAPI): ICampaign {
  return {
    ...campaign,
    startDate: new Date(campaign.startDate),
    endDate: new Date(campaign.endDate),
    createdDate: new Date(campaign.createdDate),
    lastUpdatedDate: campaign.lastUpdatedDate ? new Date(campaign.lastUpdatedDate) : undefined,
    forms: campaign.forms.map((form) => ({
      ...form,
      createdDate: new Date(form.createdDate),
      lastUpdateDate: form.lastUpdateDate ? new Date(form.lastUpdateDate) : undefined,
    })) as IForm[],
    responses: campaign.responses.map((response) => ({
      ...response,
      dateAdded: new Date(response.dateAdded),
    })) as ICampaignResponse[],
  };
}

function formatFormFields(formFields: IFormFieldRecord, contactPreference: string) {
  const formFieldsMap = new Map<string, BaseFormField>(Object.entries(formFields ?? []));

  if (formFieldsMap.size > 0) {
    const lastNameIndex = [...formFieldsMap.keys()].findIndex((field) => field === "lastName");

    if (
      !formFieldsMap.has("phone") &&
      (contactPreference === CampaignContact.Phone || contactPreference === CampaignContact.EmailAndPhone)
    ) {
      insertAtIndex(lastNameIndex + 1, "phone", phoneFormField, formFieldsMap);
    }

    if (
      !formFieldsMap.has("email") &&
      (contactPreference === CampaignContact.Email || contactPreference === CampaignContact.EmailAndPhone)
    ) {
      insertAtIndex(lastNameIndex + 1, "email", emailFormField, formFieldsMap);
    }

    insertAtIndex(0, "dateAdded", dateAddedFormField, formFieldsMap);
  }

  return Object.fromEntries(formFieldsMap) as IFormFieldRecord;
}

const sortFormFields = (a: BaseFormField, b: BaseFormField) => {
  if (a.order > b.order) return 1;
  if (a.order < b.order) return -1;
  return 0;
};

const isJobAlerts = (campaignType: CampaignTypes) =>
  campaignType === CampaignTypes.JobAlerts || campaignType === CampaignTypes.JobAlertsAndTalentNetwork;

function insertAtIndex(index: number, key: string, value: BaseFormField, map: Map<string, BaseFormField>) {
  const arr = Array.from(map);
  arr.splice(index, 0, [key, value]);
  map.clear();
  arr.forEach(([k, v]) => map.set(k, v));
}

function getDropdownOptions(currentField: BaseFormField): string[] {
  let options = new Set(currentField.dropdownOptions);

  if (currentField.fieldType === "interestPicker" && currentField.adGroupOptions) {
    options = new Set(currentField.adGroupOptions.map((option) => option.name));
  }

  return Array.from(options).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}

/**
 * Adds the contact fields to the form fields if they are missing
 */
function addContactFields(formFields: IFormFieldRecord) {
  if (!formFields["email"]) {
    formFields["email"] = defaultFormFields["email"];
  }
  if (!formFields["phone"]) {
    formFields["phone"] = defaultFormFields["phone"];
  }
  return formFields;
}

/**
 * Sets the contact fields required based on the provided contact preference
 */
function setContactFieldsRequired(formFields: IFormFieldRecord, contactPreference: CampaignContact) {
  formFields["email"].enabled = contactPreference !== CampaignContact.Phone;
  formFields["email"].required = contactPreference !== CampaignContact.Phone;
  formFields["phone"].enabled = contactPreference !== CampaignContact.Email;
  formFields["phone"].required = contactPreference !== CampaignContact.Email;
  return formFields;
}

/**
 * Adds the job alerts fields to the form fields if they are missing
 */
function addJobAlertsFields(formFields: IFormFieldRecord, campaignType: CampaignTypes) {
  if (!formFields["interestPicker"]) {
    formFields["interestPicker"] = defaultFormFields["interestPicker"];
  }
  if (!formFields["optIn"]) {
    formFields["optIn"] = defaultFormFields["optIn"];
  }
  return formFields;
}

/**
 * Sets the job alerts fields required based on the provided campaign type
 */
function setJobAlertsFieldsRequired(formFields: IFormFieldRecord, campaignType: CampaignTypes) {
  if (campaignType === CampaignTypes.JobAlerts) {
    formFields["interestPicker"].enabled = true;
    formFields["interestPicker"].required = true;
  } else if (campaignType === CampaignTypes.JobAlertsAndTalentNetwork) {
    formFields["interestPicker"].enabled = true;
    formFields["interestPicker"].required = true;
    formFields["optIn"].enabled = true;
    formFields["optIn"].required = true;
  } else {
    formFields["interestPicker"].enabled = false;
    formFields["interestPicker"].required = false;
    formFields["optIn"].enabled = false;
    formFields["optIn"].required = false;
  }
  return formFields;
}

/**
 * Handle form config with possible invalid or duplicate orders
 */
function adjustFormFieldOrders(
  formFields: IFormFieldRecord,
  campaignType: CampaignTypes,
  hasContactFields = false,
) {
  let newFormFields = setInitialFormFieldOrder(formFields, campaignType);
  newFormFields = setFormFieldOrderByCampaignType(newFormFields, campaignType, hasContactFields);
  const orderMap = toFormFieldOrderMap(newFormFields);
  if (hasDuplicateOrderKeys(orderMap)) {
    newFormFields = recursiveAdjustOrdersForDuplicateOrder(newFormFields, orderMap);
  }
  return ensureNoMissingOrder(newFormFields);
}

/**
 * Returns a new object with the form fields and any invalid orders set to default values or the index of the field in the object
 */
function setInitialFormFieldOrder(formFields: IFormFieldRecord, campaignType: CampaignTypes) {
  const defaultOrderRecord = isJobAlerts(campaignType) ? defaultJATNFormFieldOrder : defaultFormFieldOrder;

  const fieldsWithValidOrders = Object.entries(formFields).reduce((acc, [key, field], index) => {
    const isValidOrder = Number.isInteger(field.order) && field.order >= 0;
    const order = isValidOrder ? field.order : (defaultOrderRecord[key] ?? index);
    acc[key] = { ...field, order };
    return acc;
  }, {} as IFormFieldRecord);

  return recursiveSet0Index(fieldsWithValidOrders);
}

/**
 * Ensures form ordering matches required order based on the campaign type
 *
 * Attempts to maintain any user set order values, but will adjust if the order is invalid or a duplicate
 */
function setFormFieldOrderByCampaignType(
  formFields: IFormFieldRecord,
  campaignType: CampaignTypes,
  hasContactFields = false,
) {
  const fieldEntries = Object.entries(formFields);

  const emailOrder = isJobAlerts(campaignType)
    ? defaultJATNFormFieldOrder.email // Required order for job alerts
    : hasContactFields
      ? formFields["email"].order // User set order
      : formFields["lastName"].order + 1; // Default order

  if (
    formFields["email"] &&
    fieldEntries.some(([key, field]) => field.order === emailOrder && key !== "email")
  ) {
    fieldEntries
      .filter(([_, field]) => field.order >= emailOrder)
      .forEach(([key]) => {
        formFields[key].order++;
      });
    formFields["email"].order = emailOrder;
  }

  const { interestPicker: interestPickerOrder, optIn: optInOrder } = isJobAlerts(campaignType)
    ? defaultJATNFormFieldOrder
    : defaultFormFieldOrder;
  if (
    formFields["interestPicker"] &&
    fieldEntries.some(([key, field]) => field.order === interestPickerOrder && key !== "interestPicker")
  ) {
    fieldEntries
      .filter(([_, field]) => field.order >= interestPickerOrder)
      .forEach(([key, _]) => {
        formFields[key].order++;
      });
    formFields["interestPicker"].order = interestPickerOrder;
  }
  if (
    formFields["optIn"] &&
    fieldEntries.some(([key, field]) => field.order === optInOrder && key !== "optIn")
  ) {
    fieldEntries
      .filter(([key, field]) => field.order >= optInOrder && key !== "interestPicker")
      .forEach(([key, _]) => {
        formFields[key].order++;
      });
    formFields["optIn"].order = optInOrder;
  }

  let phoneOrder = formFields["phone"].order; // User set order
  if (!hasContactFields) {
    phoneOrder = isJobAlerts(campaignType) ? formFields["lastName"].order + 1 : formFields["email"].order + 1; // Default order
  }
  if (
    formFields["phone"] &&
    fieldEntries.some(([key, field]) => field.order === phoneOrder && key !== "phone")
  ) {
    fieldEntries
      .filter(([_, field]) => field.order >= phoneOrder)
      .forEach(([key, _]) => {
        formFields[key].order++;
      });
    formFields["phone"].order = phoneOrder;
  }

  return formFields;
}

/**
 * Ensures form ordering matches required order based on the campaign type
 */
function setFieldsRequiredByCampaignType(formFields: IFormFieldRecord, campaignType: CampaignTypes) {
  const isJA = isJobAlerts(campaignType);
  formFields["email"].enabled = isJA || formFields["email"].enabled;
  formFields["email"].required = isJA || formFields["email"].required;
  formFields["phone"].enabled =
    campaignType === CampaignTypes.JobAlerts ? false : formFields["phone"].enabled;
  formFields["phone"].required =
    campaignType === CampaignTypes.JobAlerts ? false : formFields["phone"].required;
  formFields["interestPicker"].enabled = isJA;
  formFields["interestPicker"].required = isJA;
  formFields["optIn"].enabled = campaignType === CampaignTypes.JobAlertsAndTalentNetwork;
  formFields["optIn"].required = campaignType === CampaignTypes.JobAlertsAndTalentNetwork;
  return formFields;
}

/**
 * Sets the fields required by the contact preference
 */
function setFieldsRequiredByContactPreference(
  formFields: IFormFieldRecord,
  contactPreference: CampaignContact,
) {
  const isPhoneRequired = contactPreference.includes(CampaignContact.Phone);
  formFields["phone"].enabled = isPhoneRequired;
  formFields["phone"].required = isPhoneRequired;
  const isEmailRequired = contactPreference.includes(CampaignContact.Email);
  formFields["email"].enabled = isEmailRequired;
  formFields["email"].required = isEmailRequired;
  return formFields;
}

/**
 * Checks if form fields are missing an order of 0 and will decrement order values until an order of 0 is found
 * @param fields The form fields to check for an order of 0
 * @returns The form fields with an order of 0
 */
function recursiveSet0Index(fields: IFormFieldRecord): IFormFieldRecord {
  const entries = Object.entries(fields);
  if (!entries.some(([_, field]) => field.order === 0)) {
    entries.forEach(([key, _]) => {
      fields[key].order = fields[key].order - 1;
    });
    return recursiveSet0Index(fields);
  }
  return fields;
}

/**
 * Maps form fields to an order map so we can identify duplicate orders
 */
function toFormFieldOrderMap(formFields: IFormFieldRecord): Map<number, string[]> {
  const orderMap = new Map<number, string[]>();
  Object.keys(formFields).forEach((key) => {
    const order = formFields[key].order;
    const mappedOrder = orderMap.get(order) || [];
    mappedOrder.push(key);
    orderMap.set(order, mappedOrder);
  });
  return orderMap;
}

/**
 * Recursively adjusts the order of form fields to ensure no duplicate orders are present
 *
 * When a duplicate order is found, the order of the field is set to the next order, and all fields with the same order or greater are incremented by 1
 *
 * The function is recursive, so at the end we check for duplicates again, and if found, it will call itself again
 */
function recursiveAdjustOrdersForDuplicateOrder(
  formFields: IFormFieldRecord,
  orderMap: Map<number, string[]>,
): IFormFieldRecord {
  orderMap.forEach((orderKeys, order) => {
    const entries = Object.entries(formFields);
    let currentOrder = order;
    orderKeys.forEach((item) => {
      if (!formFields[item]) {
        return;
      }
      if (defaultFormFieldOrder[item] === currentOrder) {
        formFields[item].order = currentOrder;
      } else {
        currentOrder++;
        if (entries.some(([_, field]) => field.order === currentOrder)) {
          entries.forEach(([key, _]) => {
            if (formFields[key].order >= currentOrder) {
              formFields[key].order++;
            }
          });
        }
        formFields[item].order = currentOrder;
      }
    });
  });
  const newOrderMap = toFormFieldOrderMap(formFields);
  if (hasDuplicateOrderKeys(newOrderMap)) {
    return recursiveAdjustOrdersForDuplicateOrder(formFields, newOrderMap);
  }
  return formFields;
}

/**
 * Draggable order is sequential 0 indexed, and requires all int values, this function ensures that the order is correct when passing to the form builder
 */
function ensureNoMissingOrder(fields: IFormFieldRecord) {
  const sortedFields = Object.entries(fields).sort((a, b) => a[1].order - b[1].order);
  let currentOrder = 0;
  for (let i = 0; i < sortedFields.length; i++) {
    const [key, field] = sortedFields[i];
    if (field.order !== currentOrder) {
      fields[key].order = currentOrder;
    }
    currentOrder++;
  }
  return fields;
}

/**
 * Updates the form fields based on the campaign type or contact preference
 */
function updateFieldsOnCampaignTypeChange(
  formFields: IFormFieldRecord,
  campaignType: CampaignTypes,
  contactPreference: CampaignContact,
) {
  return setFieldsRequiredByContactPreference(
    setFieldsRequiredByCampaignType(
      recursiveSet0Index(ensureNoMissingOrder(setFormFieldOrderByCampaignType(formFields, campaignType))),
      campaignType,
    ),
    contactPreference,
  );
}

function updateFieldsOnContactChange(formFields: IFormFieldRecord, contactPreference: CampaignContact) {
  return setFieldsRequiredByContactPreference(formFields, contactPreference);
}

/**
 * Returns true if any of the map values contain more than one key
 */
function hasDuplicateOrderKeys(orderMap: Map<number, string[]>) {
  return Array.from(orderMap.values()).some((keys) => keys.length > 1);
}

/**
 * Initial field load will add missing fields, set required fields, adjust field orders, and set field order based on campaign type
 */
function initialFieldLoad(
  formFields: IFormFieldRecord,
  campaignType: CampaignTypes,
  contactPreference: CampaignContact,
) {
  const hasContactFields = formFields["phone"] !== undefined && formFields["email"] !== undefined; // Original form fields may not have contact fields
  formFields = addMissingFields(formFields, campaignType, contactPreference);
  formFields = setFieldsRequiredByCampaignType(formFields, campaignType);
  formFields = adjustFormFieldOrders(formFields, campaignType, hasContactFields);
  return formFields;
}

/**
 * All forms will now have email, phone, interestPicker, and optIn fields added if they are missing, they will be set to required based on the campaign type
 *
 * Previously, the contact fields were added on the form rendering side, and the job alerts fields were added only to job alerts forms
 */
function addMissingFields(
  formFields: IFormFieldRecord,
  campaignType: CampaignTypes,
  contactPreference: CampaignContact,
) {
  const hasContactFields = formFields["email"] !== undefined && formFields["phone"] !== undefined;
  if (!hasContactFields) {
    formFields = addContactFields(formFields);
  }
  formFields = setContactFieldsRequired(formFields, contactPreference);

  const hasJobAlertFields = formFields["interestPicker"] !== undefined && formFields["optIn"] !== undefined;
  if (!hasJobAlertFields) {
    formFields = addJobAlertsFields(formFields, campaignType);
  }
  formFields = setJobAlertsFieldsRequired(formFields, campaignType);

  return formFields;
}

/**
 * Builds Formik initial values for the Create Form page from an imported form configuration file.
 *
 * Takes a deep clone of the input so it never mutates the caller's copy (e.g. router state). Drops
 * the source campaignId/formId/labelHistory (a brand-new campaign/form is always created on submit),
 * and clears the Interest Picker's ad group selections since they're sourced from a per-client
 * external data feed and aren't portable between environments.
 */
function buildImportedFormInitialValues(importedFormState: ICampaignFormState): ICampaignFormState {
  const cloned = cloneDeep(importedFormState);
  const rest = omit(cloned, ["campaignId", "formId", "labelHistory"]) as ICampaignFormState;
  const formFields = { ...rest.formFields };

  if (formFields.interestPicker) {
    formFields.interestPicker = { ...formFields.interestPicker, adGroupOptions: [] };
  }

  const normalizedFormFields = initialFieldLoad(formFields, rest.campaignType, rest.contactPreference);

  return {
    ...rest,
    labelHistory: {},
    formFields: normalizedFormFields,
    totalCustomFields: Object.keys(normalizedFormFields).filter((f) => f.includes("custom")).length,
    // submitMessage/submitButtonText aren't required by CampaignSchema (only enforced by the UI's
    // defaultValue fallback), so a file missing them would otherwise submit silently without one
    submitMessage: nonEmptyStringOrDefault(rest.submitMessage, InitialValues.submitMessage),
    submitButtonText: nonEmptyStringOrDefault(rest.submitButtonText, InitialValues.submitButtonText),
    // An invalid/missing endDate only survives validation when ongoing is true (CampaignSchema
    // requires it otherwise), but even then still reaches `new Date(...)` on submit and silently
    // becomes null — default it the same way useSaveCampaign/useUploadForm already do. Leave it
    // blank rather than defaulting when ongoing is false, so the existing "End Date is required"
    // validation still forces the user to pick one instead of silently accepting a 2099 fallback
    endDate: isValidDateValue(rest.endDate) ? rest.endDate : rest.ongoing ? new Date("12/31/2099") : "",
  };
}

function nonEmptyStringOrDefault(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim().length > 0 ? value : fallback;
}

function isValidDateValue(value: unknown): boolean {
  return !!value && !Number.isNaN(new Date(value as string | number | Date).getTime());
}

const SupportedFormConfigExportVersion = 1;

/**
 * Parses and validates a form configuration file selected via the Import Form control, returning
 * ready-to-use Formik initial values, or throwing a user-facing Error describing what's wrong.
 *
 * Accepts either the `{ exportVersion, formState }` envelope produced by Export, or a raw
 * `ICampaignFormState` (for hand-edited/regenerated files). Only `Form`-type configurations are
 * importable — Job Alerts and Talent Network campaigns are managed differently (a single
 * auto-generated campaign per client, campaign type locked in the UI) and aren't safe to recreate
 * through this flow.
 */
const invalidFormConfigMessage = "That file isn't a valid form configuration.";

function isPlainObject(value: unknown): value is Record<string, any> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/**
 * Every field whose type renders a fixed option list must actually have one, or the field table
 * throws while reading `dropdownOptions.length`
 */
function hasValidDropdownOptionsWhereRequired(formFields: IFormFieldRecord): boolean {
  return Object.values(formFields).every((field) => {
    if (!isPlainObject(field)) {
      return false;
    }
    return field.fieldType !== "dropdown" && field.fieldType !== "multiSelect"
      ? true
      : Array.isArray(field.dropdownOptions);
  });
}

/**
 * When an Interest Picker field's adGroupOptions is provided at all, every entry must have a
 * string type/name — remapAdGroupOptions reads both unconditionally, so a bad entry here would
 * otherwise only surface as a throw after the user has already been navigated to the Edit page
 */
function hasValidAdGroupOptionsWhereProvided(formFields: IFormFieldRecord): boolean {
  const adGroupOptions = formFields.interestPicker?.adGroupOptions;
  if (adGroupOptions === undefined) {
    return true;
  }
  return (
    Array.isArray(adGroupOptions) &&
    adGroupOptions.every(
      (adGroup) =>
        isPlainObject(adGroup) && typeof adGroup.type === "string" && typeof adGroup.name === "string",
    )
  );
}

function parseImportedFormConfig(raw: unknown): ICampaignFormState {
  const envelope = isPlainObject(raw) ? raw : null;
  const isEnvelope = !!envelope && isPlainObject(envelope.formState);

  if (isEnvelope && envelope.exportVersion !== SupportedFormConfigExportVersion) {
    throw new Error("This configuration file was exported by an unsupported version of the CRM.");
  }

  const formState = (isEnvelope ? envelope.formState : raw) as Partial<ICampaignFormState> | null;

  if (isPlainObject(formState) && formState.campaignType && formState.campaignType !== CampaignTypes.Form) {
    throw new Error(
      "Only standard Form configurations can be imported. Job Alerts and Talent Network forms are managed separately.",
    );
  }

  const formFields = isPlainObject(formState) ? (formState.formFields as IFormFieldRecord) : undefined;
  const isValid =
    isPlainObject(formState) &&
    typeof formState.campaignName === "string" &&
    formState.campaignName.trim().length > 0 &&
    formState.campaignType === CampaignTypes.Form &&
    Object.values(CampaignContact).includes(formState.contactPreference as CampaignContact) &&
    typeof formState.welcomeMessage === "string" &&
    !!formState.startDate &&
    isPlainObject(formFields) &&
    isPlainObject(formFields.firstName) &&
    isPlainObject(formFields.lastName) &&
    hasValidDropdownOptionsWhereRequired(formFields);

  if (!isValid) {
    throw new Error(invalidFormConfigMessage);
  }

  try {
    return buildImportedFormInitialValues(formState as ICampaignFormState);
  } catch {
    // Defense in depth: the shape checks above are the primary guard, but if some other
    // unanticipated shape still slips through, fail with a user-facing message rather than
    // whatever internal error normalization happened to throw
    throw new Error(invalidFormConfigMessage);
  }
}

const talentNetworkCampaignTypeFamily = [
  CampaignTypes.JobAlerts,
  CampaignTypes.TalentNetwork,
  CampaignTypes.JobAlertsAndTalentNetwork,
];

/**
 * Job Alerts / Talent Network campaigns can be freely switched between those three types via
 * `CampaignTypePicker` inside Edit, so they're treated as interchangeable for import purposes —
 * `handleCampaignEdit`'s existing `hasCareers` guard still downgrades an invalid combination at
 * submit time regardless of what's set here. `Form` only matches `Form`.
 */
function isSameCampaignTypeFamily(a: CampaignTypes, b: CampaignTypes): boolean {
  if (a === CampaignTypes.Form || b === CampaignTypes.Form) {
    return a === CampaignTypes.Form && b === CampaignTypes.Form;
  }
  return talentNetworkCampaignTypeFamily.includes(a) && talentNetworkCampaignTypeFamily.includes(b);
}

/**
 * Parses and validates a form configuration file selected via the "Import — Overwrite" control on
 * a form's detail page, returning the validated raw form state — not yet merged with the
 * destination form being overwritten, since that merge (`buildOverwriteFormInitialValues`) needs
 * the destination's own live data, which isn't available at this point.
 *
 * Unlike `parseImportedFormConfig` (create-new, Form-type only), this accepts any config whose
 * campaign type is in the same family as `targetCampaignType` — Job Alerts / Talent Network
 * campaigns can only ever be Edited, never Created, so overwriting the existing one is the only
 * way to promote their configuration across environments.
 */
function parseOverwriteFormConfig(raw: unknown, targetCampaignType: CampaignTypes): ICampaignFormState {
  const envelope = isPlainObject(raw) ? raw : null;
  const isEnvelope = !!envelope && isPlainObject(envelope.formState);

  if (isEnvelope && envelope.exportVersion !== SupportedFormConfigExportVersion) {
    throw new Error("This configuration file was exported by an unsupported version of the CRM.");
  }

  const formState = (isEnvelope ? envelope.formState : raw) as Partial<ICampaignFormState> | null;

  if (
    isPlainObject(formState) &&
    formState.campaignType &&
    !isSameCampaignTypeFamily(formState.campaignType, targetCampaignType)
  ) {
    throw new Error(
      "This file's form type doesn't match the form you're overwriting, so it can't be used here.",
    );
  }

  const formFields = isPlainObject(formState) ? (formState.formFields as IFormFieldRecord) : undefined;
  // campaignName/startDate/endDate are validated more strictly than parseImportedFormConfig's
  // equivalent (truthy-only) checks, because Job Alerts / Talent Network destinations have their
  // Name and Date inputs disabled in the Edit UI (see CreateEditInputs) — an invalid promoted
  // value has no way to be corrected there, unlike a Form-type destination the user can just
  // retype. ongoing isn't checked here — a missing/invalid value is defaulted to false in
  // buildOverwriteFormInitialValues, same as endDate.
  const startDateValid = isValidDateValue(formState?.startDate);
  const endDateValid = formState?.ongoing === true || isValidDateValue(formState?.endDate);
  const isValid =
    isPlainObject(formState) &&
    typeof formState.campaignName === "string" &&
    formState.campaignName.trim().length > 0 &&
    formState.campaignName.trim().length <= maxCampaignNameLength &&
    startDateValid &&
    endDateValid &&
    !!formState.campaignType &&
    isSameCampaignTypeFamily(formState.campaignType, targetCampaignType) &&
    Object.values(CampaignContact).includes(formState.contactPreference as CampaignContact) &&
    typeof formState.welcomeMessage === "string" &&
    isPlainObject(formFields) &&
    isPlainObject(formFields.firstName) &&
    isPlainObject(formFields.lastName) &&
    hasValidDropdownOptionsWhereRequired(formFields) &&
    hasValidAdGroupOptionsWhereProvided(formFields);

  if (!isValid) {
    throw new Error(invalidFormConfigMessage);
  }

  return formState as ICampaignFormState;
}

/**
 * Matches an imported form's Interest Picker ad group selections against the destination
 * environment's own known ad group catalog (a form's own `adGroupOptions` is always the client's
 * full catalog, not just the selected entries — confirmed against the config endpoint's real
 * response shape). Ad group ids are per-client GUIDs from an external feed, so they can't cross
 * environments as-is — matching by type+name is the only portable way to carry a selection over.
 *
 * Returns the destination's own catalog with `enabled` overlaid from any matching imported entry
 * (so the destination's ids are always the ones kept — they're the only ones valid here), plus a
 * count of imported *enabled* entries that had no match in the destination, to surface to the user.
 */
function remapAdGroupOptions(
  imported: IAdGroup[],
  destination: IAdGroup[],
): { options: IAdGroup[]; unmatchedCount: number } {
  // Tolerates a missing type/name on either side — the imported side is already validated by
  // hasValidAdGroupOptionsWhereProvided, but the destination side comes straight from the API
  const key = (adGroup: IAdGroup) =>
    `${String(adGroup?.type ?? "")
      .trim()
      .toLowerCase()}::${String(adGroup?.name ?? "")
      .trim()
      .toLowerCase()}`;
  const importedByKey = new Map(imported.map((adGroup) => [key(adGroup), adGroup]));
  const destinationKeys = new Set(destination.map(key));

  const options = destination.map((adGroup) => ({
    ...adGroup,
    enabled: importedByKey.get(key(adGroup))?.enabled ?? false,
  }));

  const unmatchedCount = imported.filter(
    (adGroup) => adGroup.enabled && !destinationKeys.has(key(adGroup)),
  ).length;

  return { options, unmatchedCount };
}

/**
 * Builds Formik initial values for overwriting an existing form from an imported configuration
 * file — promoting the file's name, schedule (startDate/endDate/ongoing), and full configuration
 * onto the destination form. Unlike `buildImportedFormInitialValues` (which always creates
 * something new), campaignId/formId/labelHistory/clientId still always come from `destination`,
 * never the import, and Interest Picker ad group selections are remapped against the
 * destination's own known catalog instead of clearing them, since for Job Alerts / Talent Network
 * forms they're the actual configuration being copied.
 */
function buildOverwriteFormInitialValues(
  imported: ICampaignFormState,
  destination: ICampaignFormState,
): { formState: ICampaignFormState; unmatchedAdGroupCount: number; overriddenLockedFieldCount: number } {
  const cloned = cloneDeep(imported);
  const rest = omit(cloned, ["campaignId", "formId", "labelHistory", "clientId"]) as ICampaignFormState;
  const formFields = { ...rest.formFields };

  // Fields the destination has already locked (custom fields with captured responses against
  // them) keep their destination Field Type — the import has no say over what a field's type
  // *was* when respondents answered it, only whether/how it's labeled and enabled going forward
  let overriddenLockedFieldCount = 0;
  for (const fieldKey of destination.lockedFields ?? []) {
    const destinationField = destination.formFields[fieldKey];
    if (formFields[fieldKey] && destinationField) {
      if (formFields[fieldKey].fieldType !== destinationField.fieldType) {
        overriddenLockedFieldCount++;
      }
      formFields[fieldKey] = {
        ...formFields[fieldKey],
        fieldType: destinationField.fieldType,
        fieldTypeLabel: destinationField.fieldTypeLabel,
        textLength: destinationField.textLength,
        dropdownOptions: destinationField.dropdownOptions,
      };
    }
  }

  const destinationInterestPicker = destination.formFields.interestPicker;
  let unmatchedAdGroupCount = 0;
  if (formFields.interestPicker) {
    const remapped = remapAdGroupOptions(
      formFields.interestPicker.adGroupOptions ?? [],
      destinationInterestPicker?.adGroupOptions ?? [],
    );
    formFields.interestPicker = { ...formFields.interestPicker, adGroupOptions: remapped.options };
    unmatchedAdGroupCount = remapped.unmatchedCount;
  } else if (destinationInterestPicker) {
    // The import didn't mention an Interest Picker at all — keep the destination's own as-is
    // (selections included), rather than treating "not mentioned" the same as "deselect
    // everything", which would silently clear a live form's ad group selections with no warning.
    // A full deep clone, not a shallow copy — the array (and its element objects) would otherwise
    // still alias the destination's own query-cache data, and JobAlertsAdGroupDisplay mutates ad
    // group entries in place when a user toggles one in the field editor
    formFields.interestPicker = cloneDeep(destinationInterestPicker);
  }

  const normalizedFormFields = initialFieldLoad(formFields, rest.campaignType, rest.contactPreference);

  const formState: ICampaignFormState = {
    ...rest,
    campaignId: destination.campaignId,
    formId: destination.formId,
    clientId: destination.clientId,
    labelHistory: destination.labelHistory ?? {},
    // Matches the field-type reconciliation above — keeps the lock icon/disabled Field Type
    // select in the editor UI in sync with the destination's own already-collected custom fields.
    // Filtered against the merged field set: lockedFields is a positional list ("customField0",
    // "customField1", ...) sized off the destination's own field count, which can outnumber the
    // import's — an unfiltered list would lock a brand-new field the user adds after submitting,
    // since custom field keys get reassigned positionally from 0
    lockedFields: (destination.lockedFields ?? []).filter((key) => !!normalizedFormFields[key]),
    formFields: normalizedFormFields,
    totalCustomFields: Object.keys(normalizedFormFields).filter((f) => f.includes("custom")).length,
    submitMessage: nonEmptyStringOrDefault(rest.submitMessage, InitialValues.submitMessage),
    submitButtonText: nonEmptyStringOrDefault(rest.submitButtonText, InitialValues.submitButtonText),
    // campaignName/startDate/endDate/ongoing are NOT pinned to the destination above — they come
    // from `...rest` (the imported file) instead, promoting the form's full identity and schedule
    // along with its configuration. This is a promote/overwrite action, not a config-only merge:
    // the user is explicitly told via the pre-fill banner to review these before saving.
    //
    // Trimmed here even though parseOverwriteFormConfig already checked the trimmed length against
    // maxCampaignNameLength — CampaignSchema's own .max() runs on the raw string, so an untrimmed
    // value that only passes because of trailing whitespace would parse clean here and then fail
    // yup with no way to fix it on a Job Alerts / Talent Network destination (Name input disabled)
    campaignName: rest.campaignName.trim(),
    // ongoing defaults to false if the file omits it — otherwise `undefined` would silently drop
    // the key from the PUT /campaigns/edit body on submit instead of writing an explicit value.
    ongoing: rest.ongoing ?? false,
    // Matches buildImportedFormInitialValues: an invalid/missing endDate only survives validation
    // when ongoing is true, but still reaches `new Date(...)` on submit and silently becomes null —
    // default it the same way, only when ongoing, so "End Date is required" still holds otherwise.
    endDate: isValidDateValue(rest.endDate) ? rest.endDate : rest.ongoing ? new Date("12/31/2099") : "",
  };

  return { formState, unmatchedAdGroupCount, overriddenLockedFieldCount };
}

export {
  addJobAlertsFields,
  buildOverwriteFormInitialValues,
  convertCampaignDates,
  formatFormFields,
  getDropdownOptions,
  initialFieldLoad,
  isJobAlerts,
  parseImportedFormConfig,
  parseOverwriteFormConfig,
  sortFormFields,
  updateFieldsOnCampaignTypeChange,
  updateFieldsOnContactChange,
};
